import { timecodeSpec, type FramerateName, type TimecodeSpec } from "./framerate.js";
import { elapsedMsFromFrames, framesFromElapsedMs, wrapFrames } from "./frames.js";

/**
 * Én avlesning av klokkene. `monotonicMs` kommer fra den native modulen og
 * er den eneste kilden til forløpt tid. `wallUtcMs` er bare til sortering,
 * visning og til å regne fram et nytt anker over en omstart.
 */
export interface ClockReading {
  readonly monotonicMs: number;
  readonly bootId: string;
  readonly wallUtcMs: number;
}

/** Øyeblikket en loggpost ble satt. Lagres på posten. Aldri tidskode. */
export type EntryStamp = ClockReading;

export type TcState = "ok" | "usikker";

export type TcSource = "manual" | "ltc" | "ble";

/** Verdien tidskoden regnes ut fra. Se docs/04-timecode.md. */
export interface TcAnchor {
  readonly tcFrames: number;
  readonly monotonicMs: number;
  readonly bootId: string;
  readonly wallUtcMs: number;
  readonly framerate: FramerateName;
  readonly dropFrame: boolean;
  readonly source: TcSource;
}

export interface ResolvedTimecode {
  readonly frames: number;
  readonly state: TcState;
}

/** Fortegnssatt differanse, rullet inn i −12…+12 timer. */
export function wrapSignedFrames(frames: number, spec: TimecodeSpec): number {
  const d = spec.framesPerDay;
  const w = wrapFrames(frames, spec);
  return w > d / 2 ? w - d : w;
}

/** Millisekunder siden lokal midnatt → frames. Til å sette ankeret på klokka. */
export function framesFromTimeOfDayMs(msSinceLocalMidnight: number, spec: TimecodeSpec): number {
  return wrapFrames(framesFromElapsedMs(msSinceLocalMidnight, spec), spec);
}

export function timeOfDayMs(date: Date): number {
  return (
    date.getHours() * 3600000 +
    date.getMinutes() * 60000 +
    date.getSeconds() * 1000 +
    date.getMilliseconds()
  );
}

/**
 * Nytt anker satt til nåværende klokkeslett. Kalles når en logg opprettes,
 * slik at det aldri finnes en post uten tidskode.
 */
export function createAnchorAtWallClock(
  reading: ClockReading,
  spec: TimecodeSpec,
  localDate: Date,
  source: TcSource = "manual",
): TcAnchor {
  return {
    tcFrames: framesFromTimeOfDayMs(timeOfDayMs(localDate), spec),
    monotonicMs: reading.monotonicMs,
    bootId: reading.bootId,
    wallUtcMs: reading.wallUtcMs,
    framerate: spec.rate.name,
    dropFrame: spec.dropFrame,
    source,
  };
}

/**
 * Tidskoden for en avlesning eller en loggpost.
 *
 * Differansen kan være negativ. Det er ikke et unntak — det er tilfellet der
 * regi setter tidskoden etter at loggingen har begynt.
 */
export function timecodeAt(
  anchor: TcAnchor,
  at: ClockReading,
  spec: TimecodeSpec,
): ResolvedTimecode {
  const sameBoot = at.bootId === anchor.bootId;
  const elapsed = sameBoot
    ? at.monotonicMs - anchor.monotonicMs
    : at.wallUtcMs - anchor.wallUtcMs;
  return {
    frames: wrapFrames(anchor.tcFrames + framesFromElapsedMs(elapsed, spec), spec),
    state: sameBoot ? "ok" : "usikker",
  };
}

/**
 * Nytt anker etter omstart av enheten. Veggklokka brukes som *differanse*,
 * ikke som absolutt verdi: tidskoden fortsetter der den var, med den
 * unøyaktigheten veggklokka har. Poster fra før omstarten er urørt og peker
 * fortsatt på det gamle ankeret.
 */
export function reanchorAfterReboot(
  previous: TcAnchor,
  reading: ClockReading,
  spec: TimecodeSpec,
): TcAnchor {
  const elapsed = reading.wallUtcMs - previous.wallUtcMs;
  return {
    ...previous,
    tcFrames: wrapFrames(previous.tcFrames + framesFromElapsedMs(elapsed, spec), spec),
    monotonicMs: reading.monotonicMs,
    bootId: reading.bootId,
    wallUtcMs: reading.wallUtcMs,
  };
}

/**
 * Samme anker, ny framerate.
 *
 * `tcFrames` er et ANTALL FRAMES, og hva det betyr avhenger av takten det
 * telles i. Byttes raten uten at tallet regnes om, leses det samme tallet i
 * en ny takt: et anker satt til 06:12:00:00 ved 25 fps ville vist
 * 03:06:00:00 ved 50 fps, og siden alle poster regnes ut fra ankeret, flyttet
 * HELE loggen seg.
 *
 * Omregningen gaar via millisekunder, saa det VISTE klokkeslettet staar
 * stille. `monotonicMs`, `bootId` og `wallUtcMs` roeres ikke — ankeret peker
 * fortsatt paa samme oeyeblikk, og postene regner forloept tid fra de samme
 * tallene som foer.
 *
 * Brukes naar framerate endres paa «privat», der det ikke finnes en prodleder
 * som eier verdien. En produksjons framerate skal ikke endres i feltappen.
 */
export function retimeAnchor(anchor: TcAnchor, spec: TimecodeSpec): TcAnchor {
  if (anchor.framerate === spec.rate.name && anchor.dropFrame === spec.dropFrame) {
    return anchor;
  }
  /*
   * Den gamle takten bygges av ANKERETS egne verdier. Den kan ikke utledes
   * av `spec` — den er den nye — og nettopp derfor lagrer ankeret sin egen
   * framerate, selv om all loepende regning gaar gjennom `spec`.
   */
  const gammel = timecodeSpec(anchor.framerate, anchor.dropFrame);
  const msSidenMidnatt = elapsedMsFromFrames(anchor.tcFrames, gammel);
  return {
    ...anchor,
    tcFrames: wrapFrames(framesFromElapsedMs(msSidenMidnatt, spec), spec),
    framerate: spec.rate.name,
    dropFrame: spec.dropFrame,
  };
}

export function needsReanchor(anchor: TcAnchor, reading: ClockReading): boolean {
  return reading.bootId !== anchor.bootId || reading.monotonicMs < anchor.monotonicMs;
}

/** Flytter ankeret et gitt antall frames. Alle poster følger med. */
export function shiftAnchor(anchor: TcAnchor, deltaFrames: number, spec: TimecodeSpec): TcAnchor {
  return { ...anchor, tcFrames: wrapFrames(anchor.tcFrames + deltaFrames, spec) };
}

/**
 * Differansen som må legges på ankeret for at tidskoden ved `at` skal bli
 * `targetFrames`. Grensesnittet viser denne før regi bekrefter, og den
 * skrives til audit_log.
 */
export function adjustmentTo(
  anchor: TcAnchor,
  targetFrames: number,
  at: ClockReading,
  spec: TimecodeSpec,
): number {
  const current = timecodeAt(anchor, at, spec).frames;
  return wrapSignedFrames(targetFrames - current, spec);
}

/**
 * Avviket mellom monoton og veggklokkebasert forløpt tid. Over noen sekunder
 * betyr det at operativsystemet har justert klokka. Monoton klokke vinner
 * alltid; dette er bare til måling.
 */
export function clockSkewMs(anchor: TcAnchor, at: ClockReading): number | null {
  if (at.bootId !== anchor.bootId) return null;
  return (at.wallUtcMs - anchor.wallUtcMs) - (at.monotonicMs - anchor.monotonicMs);
}

/**
 * Stoerste avvik veggklokka faar ha foer tidskoden er usikker.
 *
 * Ett sekund er 25 frames ved 25 fps. Mindre enn det er ikke verdt et varsel
 * regi maa forholde seg til midt i et skift; mer enn det er noe hen maa vite.
 */
export const WALL_CLOCK_TOLERANCE_MS = 1000;

export type WallClockVerdict =
  /** Veggklokka har oppfoert seg. Tidskoden kan baeres fram paa den. */
  | "trusted"
  /** Veggklokka er bak et punkt vi vet den var forbi. Alltid feil. */
  | "moved-backward"
  /** Maalt avvik mot monoton klokke innenfor samme oppstart. */
  | "skew-measured"
  /** Ingenting aa maale mot. Tidskoden baeres fram paa noe vi ikke vet. */
  | "unverified";

/**
 * Kan veggklokka stoles paa naar ankeret skal regnes fram?
 *
 * DETTE ER IKKE DET SAMME SPOERSMAALET SOM `needsReanchor`. Den sier at
 * tellingen har begynt paa nytt, og det skjer flere ganger om dagen i en
 * installert iOS-webapp: operativsystemet avliver appen i bakgrunnen, og
 * `performance.now()` starter paa null. Re-ankringen er riktig og noedvendig
 * hver gang.
 *
 * Men re-ankringen regner fram paa VEGGKLOKKA, og det er bare den som kan
 * oedelegge tidskoden. Er veggklokka i orden, er den framregnede tidskoden
 * ogsaa i orden — uansett hvor mange ganger appen er avlivet. Derfor maales
 * veggklokka her, i stedet for at hver nye oppstart antas aa vaere en feil.
 *
 * Spesifikasjonen ber selv om dette: «Drift maa maales, ikke antas.»
 *
 * Hjerteslaget er siste avlesning foer appen forsvant. Finnes det ikke, er
 * svaret `unverified` — vi naekter aa kalle noe trygt som vi ikke har maalt.
 */
export function assessWallClock(
  anchor: TcAnchor,
  heartbeat: ClockReading | null,
  now: ClockReading,
  toleranceMs: number = WALL_CLOCK_TOLERANCE_MS,
): WallClockVerdict {
  // Bakover er den farlige retningen. Loggen kan bare legges til, saa en
  // tidskode som gaar bakover setter poster i gal rekkefoelge mot materialet.
  if (now.wallUtcMs < anchor.wallUtcMs - toleranceMs) return "moved-backward";
  if (heartbeat === null) return "unverified";
  if (now.wallUtcMs < heartbeat.wallUtcMs - toleranceMs) return "moved-backward";

  /*
   * Samme oppstart som hjerteslaget: da er monoton klokke fasit, og avviket
   * kan maales i stedet for gjettes. Er boot-id-en en annen, har tellingen
   * begynt paa nytt og det finnes ikke noe maal for forloept tid — bare
   * retningen, som er sjekket over. Appen kan ha ligget avlivet i timer, og
   * det er ikke til aa skille fra at klokka hoppet framover. Det staar
   * uskrevet, ikke skjult: se docs/04-timecode.md.
   */
  if (heartbeat.bootId === now.bootId) {
    const skew = (now.wallUtcMs - heartbeat.wallUtcMs)
      - (now.monotonicMs - heartbeat.monotonicMs);
    if (Math.abs(skew) > toleranceMs) return "skew-measured";
  }
  return "trusted";
}
